import { CmsImage } from "@/components/common/CmsImage/index";
import { Subtitle } from "../Subtitle/index";
import styles from "./Hero.module.css";
import type { LandingPageComponent, SegmentComponent } from "@/types/landing";
import { DynamicForm } from "../DynamicForm";
import { Spinner } from "@/components/Spinner";
import { LeadFormPopup } from "src/components/landing/LeadFormPopup";
import { TemplateBuilderHead } from "@/components/common/Head/templateBuilderHead";
import type { TemplateStyleHead } from "@/types/templateBuilder";
import { GeoSelector } from "@/components/common/GeoSelector";
import useFeatureFlag from "src/hooks/useFeatureFlag";
import { createRoot } from "react-dom/client";
import { useEffect, useState } from "react";
import { logNewGoogleAnalyticsEvent } from "src/utils/utils";
import { useSessionData } from "../../../hooks/useCurrentSession";
import { DEFAULT_AUTHENTICATED_STRING } from "../../../constants";
import LoadingPopup from "@/components/landing/LoadingPopup";
import { color } from "@/utils/logs";

export const Hero: LandingPageComponent = ({ site, landingPage }) => {
    // TODO: when we don't need the feature flag any more, remove this block of code
    const useGeo = useFeatureFlag("ancorisFeatureFlagUseGeo");
    // END TODO

    const {
        title = "",
        subtitle = "",
        heading = "",
    } = landingPage.data?.attributes.sanitisedAUJ?.templateVars ?? landingPage.data?.attributes ?? {};

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [loaderIsOpen, setLoaderIsOpen] = useState<boolean>(false);
    const { status } = useSessionData();

    // After signing in with a social provider, the user is redirected back to the landing page
    // if we have a spinner on the page, the dynamic form does not automatically show.
    // This is an issue because the code that handles the form submit is inside the dynamic form component
    // and so after signing in with socials, the form won't submit if the spinner is configured
    // To fix this, check if the user is authenticated and if they are, open the modal
    useEffect(() => {
        if (status === DEFAULT_AUTHENTICATED_STRING) {
            setIsModalOpen(true);
        }
    }, [status]);

    const spinner = landingPage.data?.attributes?.spinner?.data?.attributes;
    const useImageHeight = landingPage.data?.attributes.useImageHeight;

    let animationStartFlag = false;
    let animationEndFlag = false;
    let bannerflow_cta_id = "";

    const templateString =
        landingPage.data?.attributes.landing_page_presentation?.data?.attributes
            .sanitisedLandingContentHTML || "";
    const templateStyleString =
        landingPage.data?.attributes.landing_page_presentation?.data?.attributes
            .sanitisedLandingContentStyles || "";

    const getStyles = (styleName: string) => {
        const styleArray = [styles[styleName]];

        if (spinner) {
            styleArray.push(styles[`${styleName}_spinner`]);
        }

        if (useImageHeight) {
            styleArray.push(styles[`${styleName}_custom_height`]);
        }

        return styleArray.join(" ");
    };

    const templateStyles: TemplateStyleHead[] = [
        { key: "landing-template-style", templateStyle: templateStyleString },
    ];

    useEffect(() => {
        const landingLogos =
            document.querySelectorAll<HTMLElement>(".frontend-logo");

        landingLogos.forEach((landingLogo) => {
            landingLogo.id = "";
            landingLogo.classList.remove(".frontend-logo");

            const root = createRoot(landingLogo);
            root.render(
                <CmsImage
                    cmsMedia={site?.attributes?.logo || null}
                    desiredHeight={66}
                    defaultAlt={site?.attributes?.name}
                />
            );
        });
    }, [site?.attributes?.logo, site?.attributes?.name]);

    const handleResize = (event: MessageEvent, sandbox: HTMLIFrameElement) => {
        if (event.data?.height > 0) {
            sandbox.style.height = `${event.data.height}px`;
        }
    };

    const handleBannerflowClick = (event: MessageEvent) => {
        if (!animationStartFlag) {
            bannerflow_cta_id = event.data?.cta_id;

            logNewGoogleAnalyticsEvent(`bannerflow-animation-start`, {
                cta_location: bannerflow_cta_id,
            });
            animationStartFlag = true;
        }
    };

    const handleBannerflowAnimationEnd = (event: MessageEvent) => {
        if (!animationEndFlag) {
            logNewGoogleAnalyticsEvent(`bannerflow-animation-end`, {
                cta_location: bannerflow_cta_id,
            });

            setIsModalOpen(true);
            animationEndFlag = true;
        }
    };

    const handleMessage = (event: MessageEvent, sandbox: HTMLIFrameElement) => {
        if (event.data?.type == "resize") {
            handleResize(event, sandbox);
        } else if (event.data?.type == "bannerflow") {
            if (event.data?.action == "click") {
                handleBannerflowClick(event);
            } else if (event.data?.action == "animationEnd") {
                handleBannerflowAnimationEnd(event);
            }
        }
    };

    useEffect(() => {
        const sandboxes =
            document.querySelectorAll<HTMLIFrameElement>(".frontend-sandbox");

        if (sandboxes.length > 0) {
            sandboxes.forEach((sandbox) => {
                sandbox.classList.add(styles.bannerflow);
                window.addEventListener(
                    "message",
                    (event) => {
                        handleMessage(event, sandbox);
                    },
                    {
                        "passive": true,
                    }
                );
            });
        }
    }, []);

    // TODO: the winning segment will need to be configurable once we move away from MVP
    const winningSegment: SegmentComponent = {
        id: 1,
        prizeText: "100 FREE SPINS",
        winnable: false,
        spinnerText: "",
        description: "",
        colour: "",
    };

    const loadingText =
        landingPage.data?.attributes?.loadingPopup?.loadingPopupText;

    return (
        <>
            <TemplateBuilderHead templateStyles={templateStyles} />
            {useGeo ? <GeoSelector position="absolute" /> : null}
            {loaderIsOpen && loadingText ? (
                <LoadingPopup loadingText={loadingText} />
            ) : null}
            {templateString ? (
                <>
                    <div dangerouslySetInnerHTML={{ __html: templateString }} />
                    <LeadFormPopup
                        winningSegment={winningSegment}
                        site={site}
                        landingPage={landingPage}
                        isModalOpen={isModalOpen}
                        setIsModalOpen={setIsModalOpen}
                        spinStartEnd={{ start: 0, end: 0 }}
                        useBannerflow={true}
                        setLoaderIsOpen={setLoaderIsOpen}
                    />
                </>
            ) : (
                <div className={getStyles("hero")}>
                    <div className={getStyles("content")}>
                        <div className={getStyles("logo")}>
                            <CmsImage
                                cmsMedia={site?.attributes?.logo || null}
                                // Once ACD-11915 merges, add max width to this image because max width is added in ACD-11915
                                desiredHeight={66}
                                defaultAlt={site?.attributes?.name}
                            />
                        </div>
                        <Subtitle
                            title={title}
                            subtitle={subtitle}
                            heading={heading}
                        />
                        {spinner ? (
                            <Spinner
                                landingPage={landingPage}
                                site={site}
                                isModalOpen={isModalOpen}
                                setIsModalOpen={setIsModalOpen}
                                setLoaderIsOpen={setLoaderIsOpen}
                            />
                        ) : (
                            <DynamicForm
                                site={site}
                                landingPage={landingPage}
                                setLoaderIsOpen={setLoaderIsOpen}
                            />
                        )}
                    </div>
                </div>
            )}
        </>
    );
};
